import { useCallback, useEffect, useState } from "react"
import { ErroreApi } from "../api/client"
import { type StatoRegia, apiRegia } from "../api/regia"
import { useTema } from "../stato/useTema"
import { Artisti } from "./regia/Artisti"
import { Diretta } from "./regia/Diretta"
import { Edizioni } from "./regia/Edizioni"
import { Gruppo } from "./regia/Gruppo"
import "./regia.css"

const CHIAVE_CODICE = "giuria-trash:regia"
/** Dove eravamo rimasti. Con più annate in archivio, ripartire ogni volta
 *  dall'edizione più recente porterebbe sulla serata sbagliata. */
const CHIAVE_SERATA = "giuria-trash:regia-serata"

/** In diretta si guarda lo schermo di sfuggita: meglio ricaricare spesso e
 *  fidarsi di quello che c'è scritto, che tenere uno stato locale ottimista. */
const RINFRESCO_MS = 3000

type Scheda = "diretta" | "artisti" | "gruppo" | "edizioni"

export function Regia() {
  const [codice, setCodice] = useState(() => localStorage.getItem(CHIAVE_CODICE) ?? "")
  const [autenticato, setAutenticato] = useState(false)

  if (!autenticato) {
    return (
      <Portineria
        codice={codice}
        onCodice={setCodice}
        onEntrato={() => {
          localStorage.setItem(CHIAVE_CODICE, codice)
          setAutenticato(true)
        }}
      />
    )
  }
  return <Banco codice={codice} onEsci={() => setAutenticato(false)} />
}

/** L'ingresso della regia. Il codice sta in una variabile d'ambiente del
 *  server ed è unico per tutte le edizioni: non è una password per persona. */
function Portineria({
  codice,
  onCodice,
  onEntrato,
}: {
  codice: string
  onCodice: (c: string) => void
  onEntrato: () => void
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function verifica(ev: React.FormEvent) {
    ev.preventDefault()
    setInCorso(true)
    setErrore(null)
    try {
      await apiRegia.edizioni(codice)
      onEntrato()
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 403) setErrore("Codice di regia non valido.")
      else setErrore(e instanceof Error ? e.message : "Non è stato possibile entrare.")
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form className="schermo regia regia--ingresso" onSubmit={verifica}>
      <p className="occhiello">Pannello di regia</p>
      <h1 className="regia__titolo">Chi canta adesso</h1>
      <input
        className="campo regia__codice"
        type="password"
        value={codice}
        onChange={(e) => onCodice(e.target.value)}
        placeholder="Codice di regia"
        autoComplete="current-password"
        required
      />
      {errore && <p className="avviso">{errore}</p>}
      <button className="bottone primario regia__entra" type="submit" disabled={inCorso}>
        {inCorso ? "Verifico…" : "Entra in regia"}
      </button>
    </form>
  )
}

function Banco({ codice, onEsci }: { codice: string; onEsci: () => void }) {
  const [stato, setStato] = useState<StatoRegia | null>(null)
  const [serataId, setSerataId] = useState<string | null>(
    () => localStorage.getItem(CHIAVE_SERATA),
  )
  const [scheda, setScheda] = useState<Scheda>("diretta")
  const [errore, setErrore] = useState<string | null>(null)
  /** Non è un errore: è un'installazione nuova, e la si distingue
   *  dal caricamento in corso per non mostrare le schede troppo presto. */
  const [senzaEdizione, setSenzaEdizione] = useState(false)

  const carica = useCallback(
    async (id?: string) => {
      const bersaglio = id ?? serataId
      try {
        const fresco = bersaglio
          ? await apiRegia.stato(bersaglio, codice)
          : await apiRegia.partenza(codice)
        setStato(fresco)
        setSerataId(fresco.serata.id)
        localStorage.setItem(CHIAVE_SERATA, fresco.serata.id)
        setErrore(null)
        setSenzaEdizione(false)
      } catch (e) {
        if (e instanceof ErroreApi && e.codice === 403) onEsci()
        else if (e instanceof ErroreApi && e.codice === 404) {
          // La serata ricordata può essere sparita: si riparte dall'inizio.
          localStorage.removeItem(CHIAVE_SERATA)
          if (bersaglio) {
            setSerataId(null)
            return
          }
          // Prima installazione: lo dice lo stato vuoto della scheda
          // Edizioni, con molte meno parole e senza allarmare.
          setSenzaEdizione(true)
          setErrore(null)
        }
        else setErrore(e instanceof Error ? e.message : "Non riesco a leggere lo stato.")
      }
    },
    [serataId, codice, onEsci],
  )

  useEffect(() => {
    if (!serataId) void carica()
  }, [serataId, carica])

  // Il rinfresco automatico serve solo in diretta: nelle altre schede si sta
  // scrivendo, e ricaricare sotto le dita farebbe perdere quello che si digita.
  useEffect(() => {
    if (!serataId || scheda !== "diretta") return
    const t = setInterval(() => void carica(), RINFRESCO_MS)
    return () => clearInterval(t)
  }, [serataId, scheda, carica])

  // Senza edizione la scheda diventa davvero Edizioni, non solo di facciata:
  // altrimenti appena l'annata esiste il pannello saltava su Diretta da solo,
  // portandosi via la conferma appena comparsa.
  useEffect(() => {
    if (senzaEdizione) setScheda("edizioni")
  }, [senzaEdizione])

  useTema(stato?.serata.status === "live")

  const comando = useCallback(
    async (azione: () => Promise<unknown>) => {
      try {
        await azione()
        await carica()
      } catch (e) {
        setErrore(e instanceof Error ? e.message : "Comando non riuscito.")
      }
    },
    [carica],
  )

  const vaiA = useCallback(
    (nuovaSerata: string) => {
      setSerataId(nuovaSerata)
      setScheda("diretta")
      void carica(nuovaSerata)
    },
    [carica],
  )

  // Prima di sapere se esiste un'edizione non si può mostrare niente di
  // sensato, ma è un istante: appena la risposta arriva si sa già tutto.
  if (!stato && !senzaEdizione && !errore) {
    return (
      <div className="schermo regia">
        <p className="occhiello">Pannello di regia</p>
        <p>Carico…</p>
      </div>
    )
  }

  // Senza edizione si entra comunque, con la sola scheda che serve. Prima il
  // pannello mostrava "creane una dalla scheda Edizioni" *senza* le schede:
  // un'installazione nuova non aveva modo di partire.
  const schedaViva: Scheda = stato ? scheda : "edizioni"

  return (
    <div className="schermo regia regia--banco">
      <header className="regia__testata">
        <div className="regia__marchio">
          <p className="occhiello">Regia{stato && ` · ${stato.edizione.name}`}</p>
        </div>
        <a className="bottone regia__uscita" href="/">
          Vista giurato
        </a>
      </header>

      <nav className="regia__schede" role="tablist" aria-label="Sezioni della regia">
        {(
          [
            ["diretta", "Diretta"],
            ["artisti", "Artisti"],
            ["gruppo", "Gruppo"],
            ["edizioni", "Edizioni"],
          ] as const
        ).map(([chiave, etichetta]) => (
          <button
            key={chiave}
            type="button"
            role="tab"
            aria-selected={schedaViva === chiave}
            className="regia__scheda"
            // Finché non c'è un'edizione, Diretta, Artisti e Gruppo non
            // hanno niente da mostrare: restano visibili ma spente, così si
            // vede dove si andrà a finire senza poterci sbattere contro.
            disabled={!stato && chiave !== "edizioni"}
            onClick={() => setScheda(chiave)}
          >
            {etichetta}
          </button>
        ))}
      </nav>

      {errore && <p className="avviso">{errore}</p>}

      {stato && schedaViva === "diretta" && (
        <Diretta stato={stato} codice={codice} onSerata={vaiA} comando={comando} />
      )}
      {stato && schedaViva === "artisti" && (
        <Artisti stato={stato} codice={codice} comando={comando} />
      )}
      {stato && schedaViva === "gruppo" && (
        <Gruppo stato={stato} codice={codice} comando={comando} />
      )}
      {schedaViva === "edizioni" && (
        <Edizioni stato={stato} codice={codice} onVaiA={vaiA} comando={comando} />
      )}
    </div>
  )
}
